import { buildSeoPageCanonical } from './seoPage.js';

const FORBIDDEN_SCHEMA_KEYS = new Set([
  'patient', 'patient_name', 'patient_email', 'patient_phone', 'cpf', 'document',
  'clinical_text', 'report_text', 'diagnosis', 'conclusion', 'notes', 'content', 'raw_content',
]);

const sanitizeSchema = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeSchema);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !FORBIDDEN_SCHEMA_KEYS.has(String(key).toLowerCase()))
    .map(([key, nested]) => [key, sanitizeSchema(nested)]));
};

export const safeJsonLd = (value) => JSON.stringify(sanitizeSchema(value)).replace(/</g, '\\u003c');

export const applySeoHead = ({ title, description, canonical, robots = 'index,follow' }) => {
  if (typeof document === 'undefined') return;
  document.title = title;
  const ensureMeta = (selector, attrs) => {
    let node = document.head.querySelector(selector);
    if (!node) {
      node = document.createElement('meta');
      Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
      document.head.appendChild(node);
    }
    return node;
  };
  ensureMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', description || '');
  ensureMeta('meta[name="robots"]', { name: 'robots' }).setAttribute('content', robots);
  ensureMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', title || '');
  ensureMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', description || '');
  ensureMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', canonical || '');
  let canonicalNode = document.head.querySelector('link[rel="canonical"]');
  if (!canonicalNode) {
    canonicalNode = document.createElement('link');
    canonicalNode.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalNode);
  }
  canonicalNode.setAttribute('href', canonical || '');
};

export const buildWebPageSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: page.h1 || page.title,
  headline: page.title,
  description: page.meta_description,
  url: buildSeoPageCanonical(page.path),
  inLanguage: 'pt-BR',
  isPartOf: { '@type': 'WebSite', name: 'Laudando', url: 'https://laudando.online/' },
});

export const buildArticleSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: page.h1 || page.title,
  description: page.meta_description,
  url: buildSeoPageCanonical(page.path),
  inLanguage: 'pt-BR',
  author: page.author?.name ? { '@type': 'Person', name: page.author.name } : undefined,
  datePublished: page.published_at || undefined,
  dateModified: page.reviewed_at || undefined,
  publisher: { '@type': 'Organization', name: 'Laudando', url: 'https://laudando.online/' },
});

export const buildBreadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: buildSeoPageCanonical(item.path),
  })),
});
